/**
 * Storage Service
 * Uses Cloudflare R2 for file uploads (free bandwidth)
 */

import { S3Client, PutObjectCommand, DeleteObjectCommand, GetObjectCommand, CopyObjectCommand } from '@aws-sdk/client-s3';

// Access env vars directly - the `as any` cast broke Vite's define replacement
const R2_ACCOUNT_ID = import.meta.env.VITE_R2_ACCOUNT_ID;
const R2_ACCESS_KEY_ID = import.meta.env.VITE_R2_ACCESS_KEY_ID;
const R2_SECRET_ACCESS_KEY = import.meta.env.VITE_R2_SECRET_ACCESS_KEY;
const R2_BUCKET_NAME = import.meta.env.VITE_R2_BUCKET_NAME;
const R2_PUBLIC_URL = import.meta.env.VITE_R2_PUBLIC_URL;

// Use proxied URLs through ezart.ai instead of direct R2 URLs
const USE_PROXY = import.meta.env.VITE_USE_MEDIA_PROXY === 'true';
const PROXY_BASE_URL = import.meta.env.VITE_MEDIA_PROXY_URL || 'https://ezart.ai/api/media';

// Debug: Check what values we're getting
console.log('🔧 R2 Storage Config:', {
    ACCOUNT_ID: R2_ACCOUNT_ID ? '✅ Defined' : '❌ Undefined',
    ACCESS_KEY_ID: R2_ACCESS_KEY_ID ? '✅ Defined' : '❌ Undefined',
    SECRET_ACCESS_KEY: R2_SECRET_ACCESS_KEY ? '✅ Defined' : '❌ Undefined',
    BUCKET_NAME: R2_BUCKET_NAME ? '✅ Defined' : '❌ Undefined',
    PUBLIC_URL: R2_PUBLIC_URL ? '✅ Defined' : '❌ Undefined',
});

const S3 = new S3Client({
    region: 'auto',
    endpoint: `https://${R2_ACCOUNT_ID}.r2.cloudflarestorage.com`,
    credentials: {
        accessKeyId: R2_ACCESS_KEY_ID!,
        secretAccessKey: R2_SECRET_ACCESS_KEY!,
    },
});

/**
 * Convert an R2 URL to a proxied URL through ezart.ai
 * @param r2Url - Direct R2 URL (e.g., https://pub-xxx.r2.dev/path/to/image.jpg)
 * @returns Proxied URL (e.g., https://ezart.ai/api/media/path/to/image.jpg)
 */
const toProxiedUrl = (r2Url: string): string => {
    if (!USE_PROXY) return r2Url;

    try {
        const url = new URL(r2Url);
        const path = url.pathname.substring(1); // Remove leading slash
        return `${PROXY_BASE_URL}/${path}`;
    } catch (error) {
        console.warn('Failed to convert to proxied URL:', error);
        return r2Url; // Fallback to original URL
    }
};

/**
 * Convert R2 URLs to proxied URLs for display
 * Use this to convert existing URLs from the database
 * @param url - Image URL (can be R2 or already proxied)
 * @returns Proxied URL if USE_PROXY is enabled
 */
export const getDisplayUrl = (url: string | null | undefined): string => {
    if (!url) return '';
    if (!USE_PROXY) return url;

    // If already proxied, return as-is
    if (url.includes('/api/media/') || url.includes('ezart.ai')) {
        return url;
    }

    // Convert R2 URL to proxied URL
    return toProxiedUrl(url);
};

export const uploadToR2 = async (file: File, path: string, userId?: string): Promise<string> => {
    if (!R2_ACCOUNT_ID || !R2_ACCESS_KEY_ID || !R2_SECRET_ACCESS_KEY) {
        throw new Error('R2 credentials missing. Check environment variables.');
    }

    const filename = file.name.replace(/[^a-zA-Z0-9.-]/g, '');
    const key = userId
        ? `${path}/${Date.now()}-${userId}-${filename}`
        : `${path}/${Date.now()}-${filename}`;

    console.log('📤 Uploading to R2:', key);

    try {
        // Convert File to Uint8Array for browser compatibility
        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        const command = new PutObjectCommand({
            Bucket: R2_BUCKET_NAME,
            Key: key,
            Body: uint8Array,
            ContentType: file.type,
        });

        await S3.send(command);

        const r2Url = `${R2_PUBLIC_URL}/${key}`;
        const publicUrl = toProxiedUrl(r2Url);
        console.log('✅ Upload successful:', publicUrl);
        return publicUrl;

    } catch (error) {
        console.error('❌ R2 Upload Error:', error);
        throw error;
    }
};

export const deleteFromR2 = async (imageUrl: string): Promise<void> => {
    if (!R2_BUCKET_NAME) throw new Error("R2 config missing");

    try {
        // Extract key from URL
        // Handle both direct R2 URLs (https://pub-xxx.r2.dev/path) and proxied URLs (https://ezart.ai/api/media/path)
        let key: string;

        if (imageUrl.includes('/api/media/')) {
            // Extract path from proxied URL: https://ezart.ai/api/media/path/to/image.jpg -> path/to/image.jpg
            const parts = imageUrl.split('/api/media/');
            key = parts[1];
        } else {
            // Extract key from direct R2 URL
            const url = new URL(imageUrl);
            key = url.pathname.substring(1); // Remove leading slash
        }

        console.log('🗑️ Deleting from R2:', key);

        const command = new DeleteObjectCommand({
            Bucket: R2_BUCKET_NAME,
            Key: key,
        });

        await S3.send(command);
        console.log('✅ R2 Delete successful');
    } catch (error) {
        console.error('❌ R2 Delete Error:', error);
        throw error;
    }
};

/**
 * Copy an object within R2 from one path to another
 * Uses S3 CopyObject which doesn't have CORS issues (unlike browser fetch)
 */
export const copyInR2 = async (sourceUrl: string, destPath: string): Promise<string> => {
    if (!R2_BUCKET_NAME) throw new Error("R2 config missing");

    try {
        // Extract source key from URL
        const url = new URL(sourceUrl);
        const sourceKey = url.pathname.substring(1); // Remove leading slash

        // Extract filename from source key
        const filename = sourceKey.split('/').pop() || `copied_${Date.now()}.png`;
        const destKey = `${destPath}/${filename}`;

        console.log('📋 Copying in R2:', sourceKey, '→', destKey);

        const command = new CopyObjectCommand({
            Bucket: R2_BUCKET_NAME,
            CopySource: `${R2_BUCKET_NAME}/${sourceKey}`,
            Key: destKey,
        });

        await S3.send(command);

        const r2Url = `${R2_PUBLIC_URL}/${destKey}`;
        const publicUrl = toProxiedUrl(r2Url);
        console.log('✅ R2 Copy successful:', publicUrl);
        return publicUrl;
    } catch (error) {
        console.error('❌ R2 Copy Error:', error);
        throw error;
    }
};
